import { extensionsForSms, TipTapEditor } from '@systemeio/tip-tap';
import { FieldErrorAndDescription, FormInput, FormSelect, toast } from '@systemeio/ui';
import { useTwilioPhoneNumbers } from 'modules/profile/integrations/twilio/hooks/use-twilio-phone-numbers';
import React, { useEffect } from 'react';
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { deepCompare } from 'shared/utils/deepCompare';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { twJoin, twMerge } from 'tailwind-merge';
import { useSmsSubstitutes } from '../hooks/use-sms-substitutes';
import { useSmsTemplateActions } from '../hooks/use-sms-template-actions';
import { SmsTemplateCreateInterface, SmsTemplateId, SmsTemplateUpdateInterface } from '../types';
import { SmsSubstitutesBlock } from './sms-substitutes-block';
import TransLoco from 'shared/components/trans-loco';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
const defaultErrors = {
  name: '',
  phoneNumber: '',
  content: ''
};
const defaultState = {
  name: '',
  phoneNumber: '',
  content: ''
};
export interface SmsTemplateFormProps {
  templateId?: SmsTemplateId;
  data?: SmsTemplateCreateInterface;
  inModal?: boolean;
  disabled?: boolean;
  onStatusChange?: ({
    isFetching
  }: {
    isFetching: boolean;
  }) => void;
  onSuccess?: (_: SmsTemplateUpdateInterface) => Promise<void>;
}
SmsTemplateForm.formId = 'sms-template-form';
export function SmsTemplateForm({
  templateId,
  data,
  inModal,
  disabled,
  onStatusChange,
  onSuccess
}: SmsTemplateFormProps) {
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [tempState, setTempState] = React.useState<SmsTemplateCreateInterface>(defaultState);
  const [errors, setErrors] = React.useState(defaultErrors);
  const {
    t
  } = useLocoTranslation();
  const {
    data: twilioPhoneNumbers,
    errorNotFound: twilioPhoneNumbersNotFoundError
  } = useTwilioPhoneNumbers();
  const {
    createSmsTemplate,
    updateSmsTemplate
  } = useSmsTemplateActions();
  const {
    revalidateModules
  } = useRevalidateModules();
  useEffect(() => {
    if (data) {
      setTempState({
        name: data.name,
        phoneNumber: data.phoneNumber,
        content: data.content
      });
    }
  }, [data?.name, data?.phoneNumber, data?.content]);
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({
        isFetching
      });
    }
  }, [isFetching]);
  const phoneNumbers = !!twilioPhoneNumbersNotFoundError ? [] : twilioPhoneNumbers?.map(phoneNumber => ({
    id: phoneNumber,
    caption: phoneNumber
  }));
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFetching) return;
    if (isPreFetching) return;
    if (disabled) return;
    setIsFetching(true);
    setErrors(defaultErrors);
    try {
      let response: SmsTemplateUpdateInterface;
      if (templateId) {
        response = await updateSmsTemplate({
          id: templateId,
          ...tempState,
          phoneNumber: tempState.phoneNumber
        });
        await revalidateModules([RequestModuleEnum.SmsTemplates]);
        await onSuccess?.(response);
        toast.success(t('dashboard.sms.sms_template_successfully_updated'));
      } else {
        response = await createSmsTemplate({
          ...tempState,
          phoneNumber: tempState.phoneNumber
        });
        await revalidateModules([RequestModuleEnum.SmsTemplates]);
        await onSuccess?.(response);
        toast.success(t('dashboard.sms.sms_template_successfully_created'));
      }
    } catch (error) {
      handle422Error(error, setErrors);
    } finally {
      setIsFetching(false);
    }
  };
  const isDataBeingChanged = React.useMemo(() => {
    if (!templateId) return !deepCompare(defaultState, {
      name: tempState.name,
      phoneNumber: tempState.phoneNumber,
      content: tempState.content
    });else return !deepCompare({
      name: data?.name,
      phoneNumber: data?.phoneNumber,
      content: data?.content
    }, {
      name: tempState.name,
      phoneNumber: tempState.phoneNumber,
      content: tempState.content
    });
  }, [templateId, data, tempState]);
  const {
    substitutionData
  } = useSmsSubstitutes();
  const isPreFetching = templateId ? !data : false;
  return <form id={SmsTemplateForm.formId} className={'flex flex-col gap-3'} onSubmit={handleSubmit}>
      <div className={'flex flex-col gap-5 lg:flex-row'}>
        <div className={`min-w-0 lg:h-auto lg:basis-3/4`}>
          <div className={twMerge('bg-background-secondary flex w-full flex-col gap-5 rounded-md p-5', inModal && 'p-0')}>
            <div className={`flex basis-full flex-col gap-1`}>
              <span className="text-sm font-medium">{t('dashboard.sms.body')}</span>
              <TipTapEditor mode="text" className={twJoin(errors.content && 'border-danger')} extensions={extensionsForSms} isPreFetching={isPreFetching} isEditable={!disabled && !isFetching} content={tempState.content} onChange={content => setTempState({
              ...tempState,
              content
            })} substitutions={substitutionData} characterCountHint={t('dashboard.simple_mail_editor.character_count.tooltip')} />
              <FieldErrorAndDescription error={errors.content} />
            </div>
          </div>
        </div>
        <div className={`static top-5 flex h-fit flex-col gap-5 sm:min-w-[320px] lg:sticky lg:basis-1/4`}>
          <div className={twMerge('bg-background-secondary flex w-full flex-col gap-5 rounded-md p-5', inModal && 'border-text-secondary/30 rounded-lg border')}>
            <FormInput label={t('dashboard.sms.template_name')} value={tempState.name} isPreFetching={isPreFetching} disabled={disabled || isFetching} onChange={e_0 => setTempState({
            ...tempState,
            name: e_0.target.value
          })} required error={errors.name} />
          </div>

          <div className={twMerge('bg-background-secondary flex w-full flex-col gap-5 rounded-md p-5', inModal && 'border-text-secondary/30 rounded-lg border')}>
            <FormSelect label={t('dashboard.sms.sender_phone_number')} value={tempState.phoneNumber} data={phoneNumbers} isPreFetching={isPreFetching} disabled={disabled || isFetching} onChange={value => setTempState({
            ...tempState,
            phoneNumber: value || ''
          })} required error={errors.phoneNumber || (twilioPhoneNumbersNotFoundError ? <TransLoco t={t} i18nKey={'dashboard.sms.twilio_not_found'} components={{
            a: <LinkWithoutPrefetch target={'_blank'} className={'primary-link'} href={`/profile/integrations`} />
          }} /> : undefined)} withoutCloseIcon={!tempState.phoneNumber} />
          </div>

          <SmsSubstitutesBlock inModal={inModal} />
        </div>
      </div>

      <ConfirmationModalBeforeExit confirmationText={t('dashboard.sms.confirmation_message_before_exit')} hideModal={!isDataBeingChanged || isFetching} />
    </form>;
}